<template>
  <div class="content bg8">
    <div class="about-shell">
      <section class="about-hero">
        <div class="about-hero-copy">
          <div class="eyebrow">About Foclarity</div>
          <h1>Built to help founders get focused on what matters most.</h1>
          <p class="hero-lead">
            Foclarity exists because too many founders and small teams are trying to grow without a clear system
            for priorities, ownership, and follow-through.
          </p>
          <p class="hero-sub">
            What began as hands-on founder advising evolved into a platform for capturing the most important work,
            aligning people around it, and using AI where it actually helps.
          </p>
        </div>
        <div class="about-hero-art">
          <div class="hero-image-card">
            <img src="@/assets/mash-about.jpg" alt="Mash Ashley" class="hero-image" />
          </div>
        </div>
      </section>

      <section class="about-intro">
        <div class="intro-card">
          <div class="eyebrow">Why It Exists</div>
          <h2>Foclarity came out of real founder work, not a trend.</h2>
          <p>
            Working 1:1 with founders, solopreneurs, and small teams made one thing obvious:
            the biggest challenge was rarely a lack of ideas. It was a lack of focus, clarity, and alignment.
          </p>
          <p>
            Founders often know a lot needs to happen, but without a shared structure, priorities get muddled,
            ownership gets fuzzy, and progress becomes hard to see. Foclarity was built to create that structure.
          </p>
        </div>
        <div class="intro-card accent">
          <div class="eyebrow">What We Believe</div>
          <h2>Small teams do better when the important work is visible.</h2>
          <ul>
            <li>Clear priorities beat endless lists of good ideas.</li>
            <li>Shared ownership beats vague accountability.</li>
            <li>Visible progress beats guesswork.</li>
            <li>AI should support the work, not distract from it.</li>
          </ul>
        </div>
      </section>

      <section class="founder-section">
        <div class="eyebrow">Founder</div>
        <div class="founder-grid">
          <div class="founder-photo-card">
            <img src="@/assets/mash-michael-ashley.png" alt="Mash Michael Ashley" class="founder-photo" />
            <div class="founder-links">
              <a href="https://www.linkedin.com/in/mashley" target="_blank" rel="nofollow">LinkedIn</a>
              <a href="https://www.inspiringfounders.com" target="_blank" rel="nofollow">Peer Groups</a>
            </div>
          </div>
          <div class="founder-copy">
            <h2>Mash (Michael Ashley), Founder and CEO</h2>
            <p>
              Mash founded Foclarity after years of helping leaders and startup teams navigate the gap between strategy and execution.
              With more than 25 years at the intersection of innovation, growth, and operations, he has co-founded and scaled startups,
              contributed to multiple successful exits, guided dozens of companies from concept to traction, and launched a university-based incubator at San José State University.
            </p>
            <p>
              As an Adjunct Professor at San José State’s College of Business, Mash has taught and mentored hundreds of aspiring founders.
              His work blends real-world entrepreneurial experience with practical frameworks that help people cut through confusion and move forward.
            </p>
            <p>
              Today, he brings that same approach to Foclarity: helping founders get clear on what matters most, structuring that work into a usable system,
              and combining advising, peer support, and software to keep momentum going.
            </p>
          </div>
        </div>
      </section>

      <section class="fit-section">
        <div class="fit-copy">
          <div class="eyebrow">Who We Help</div>
          <h2>Best for founders, owner-operators, and small teams who need more structure around growth.</h2>
          <p>
            Foclarity is especially valuable when you are trying to grow with more intention, reduce chaos,
            and create a clearer operating rhythm around the priorities that matter most.
          </p>
        </div>
        <div class="fit-list">
          <div class="fit-item">Early founders trying to make better tradeoffs</div>
          <div class="fit-item">Solopreneurs who need more focus and structure</div>
          <div class="fit-item">Owner-operators juggling too many moving parts</div>
          <div class="fit-item">Small teams that need clearer alignment and accountability</div>
        </div>
      </section>

      <section class="team-section">
        <div class="section-head">
          <div class="eyebrow">Team</div>
          <h2>The people helping shape Foclarity</h2>
        </div>
        <div class="team-grid">
          <article class="team-card">
            <img src="@/assets/nicole-witt.jpeg" alt="Nicole Witt" class="team-photo" />
            <h3>Nicole Witt</h3>
            <p class="team-role">Product Engineering Manager</p>
            <p>
              Nicole helps lead product and engineering execution, with a focus on strengthening the platform, improving workflows,
              and building systems that support better onboarding, reporting, and delivery.
            </p>
            <a href="https://www.linkedin.com/in/nicolenwitt/" target="_blank" rel="nofollow">LinkedIn</a>
          </article>
          <article class="team-card">
            <img src="@/assets/nick-huang.jpeg" alt="Nick Huang" class="team-photo" />
            <h3>Nick Huang</h3>
            <p class="team-role">Digital Marketing Intern</p>
            <p>
              Nick is part of the Foclarity internship team, contributing marketing support while building experience around messaging,
              audience development, and practical execution.
            </p>
            <a href="https://www.linkedin.com/in/nicholas-huang-34a0a3238/" target="_blank" rel="nofollow">LinkedIn</a>
          </article>
        </div>
      </section>

      <section class="hiring-band">
        <p>
          We are hiring and growing. Connect with
          <a href="https://www.linkedin.com/company/foclarity/" target="_blank" rel="nofollow">Foclarity on LinkedIn</a>
          to learn more.
        </p>
      </section>

      <section class="cta-band">
        <div class="cta-copy">
          <div class="eyebrow">Next Step</div>
          <h2>Need help getting focused and moving forward?</h2>
          <p>
            If you want more clarity, alignment, and traction in your business, let’s talk about how Foclarity could support your work.
          </p>
        </div>
        <div class="cta-actions">
          <a href="/contact" class="cta-link">
            <button class="cta-btn primary">Schedule a Call</button>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  title: 'About Foclarity | Founder Focus, Clarity and Alignment',
  description: 'Learn why Foclarity exists, how Mash Ashley built it from real founder advising work, and who the platform is designed to help.',
  name: 'About',
};
</script>

<style lang="scss" scoped>
.about-shell {
  max-width: 1160px;
  margin: 0 auto;
  padding: 48px 24px 72px;
}

.eyebrow {
  color: #9a6b00;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.about-hero {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 34px;
  align-items: center;
  padding: 28px 0 42px;
  margin-bottom: 12px;
}

.intro-card,
.founder-section,
.fit-section,
.team-section,
.hiring-band,
.cta-band {
  background: #fff;
  border: 1px solid #dde5ec;
  border-radius: 28px;
  box-shadow: 0 16px 36px rgba(13, 34, 56, 0.05);
}

.about-hero-copy {
  padding: 8px 0;
}

.about-hero-copy h1 {
  margin: 0 0 18px;
  color: #10263d;
  font-size: clamp(2.4rem, 4vw, 4.2rem);
  line-height: 1.05;
}

.hero-lead {
  color: #29435c;
  font-size: 1.15rem;
  line-height: 1.65;
  margin-bottom: 14px;
}

.hero-sub {
  color: #5b7084;
  line-height: 1.7;
  margin: 0;
}

.about-hero-art {
  display: flex;
  justify-content: center;
}

.hero-image-card {
  width: 100%;
  max-width: 460px;
  background: linear-gradient(180deg, #ffffff, #eef4f8);
  border: 1px solid #d6e1ea;
  border-radius: 28px;
  padding: 18px;
  box-shadow: 0 30px 70px rgba(16, 38, 61, 0.14);
}

.hero-image {
  display: block;
  width: 100%;
  border-radius: 20px;
}

.about-intro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 28px;
}

.intro-card {
  padding: 28px;
}

.intro-card h2,
.founder-copy h2,
.fit-copy h2,
.section-head h2,
.cta-copy h2 {
  color: #12304b;
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  line-height: 1.15;
  margin: 0 0 16px;
}

.intro-card p,
.founder-copy p,
.fit-copy p,
.team-card p,
.cta-copy p,
.hiring-band p {
  color: #52677b;
  line-height: 1.75;
  margin: 0 0 14px;
}

.intro-card.accent {
  background: linear-gradient(135deg, #12395b, #184d78);
  border-color: #184d78;
}

.intro-card.accent,
.intro-card.accent h2,
.intro-card.accent p,
.intro-card.accent li,
.intro-card.accent .eyebrow {
  color: #fff;
}

.intro-card ul {
  margin: 0;
  padding-left: 20px;
}

.intro-card li {
  line-height: 1.8;
  margin-bottom: 8px;
}

.founder-section,
.fit-section,
.team-section,
.hiring-band,
.cta-band {
  padding: 30px;
  margin-bottom: 28px;
}

.founder-grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 28px;
  align-items: start;
}

.founder-photo-card {
  background: linear-gradient(180deg, #f8fbfd, #eef4f8);
  border: 1px solid #d8e2ea;
  border-radius: 24px;
  padding: 20px;
  text-align: center;
}

.founder-photo {
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 14px 30px rgba(16, 38, 61, 0.12);
}

.founder-links {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.founder-links a,
.team-card a,
.hiring-band a {
  color: #0e5a87;
  font-weight: 700;
  text-decoration: none;
}

.fit-section {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 28px;
  align-items: start;
}

.fit-list {
  display: grid;
  gap: 12px;
}

.fit-item {
  background: linear-gradient(180deg, #f8fbfd, #eef4f8);
  border: 1px solid #d7e1e8;
  border-radius: 18px;
  padding: 16px 18px;
  color: #29435c;
  font-weight: 600;
  line-height: 1.5;
}

.section-head {
  margin-bottom: 20px;
}

.team-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.team-card {
  background: linear-gradient(180deg, #ffffff, #f7fafc);
  border: 1px solid #d8e2ea;
  border-radius: 22px;
  padding: 24px;
}

.team-photo {
  width: 92px;
  height: 92px;
  object-fit: cover;
  border-radius: 50%;
  margin-bottom: 16px;
}

.team-card h3 {
  color: #12395b;
  margin: 0 0 6px;
}

.team-role {
  color: #8a6110;
  font-weight: 700;
  margin-bottom: 12px;
}

.hiring-band {
  background: linear-gradient(135deg, #f8fbfd, #f4f8fb);
}

.hiring-band p {
  margin: 0;
}

.cta-band {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
  background: linear-gradient(135deg, #fff7e6, #fff1d2);
  border-color: #ecd8a4;
}

.cta-actions {
  display: flex;
  align-items: center;
}

.cta-link {
  text-decoration: none;
}

.cta-btn {
  border: none;
  border-radius: 999px;
  padding: 14px 24px;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
}

.cta-btn.primary {
  background: linear-gradient(135deg, #12395b, #246794);
  color: #fff;
  box-shadow: 0 14px 30px rgba(18, 57, 91, 0.24);
}

@media (max-width: 980px) {
  .about-hero,
  .about-intro,
  .founder-grid,
  .fit-section,
  .cta-band,
  .team-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 680px) {
  .about-shell {
    padding: 28px 16px 56px;
  }

  .intro-card,
  .founder-section,
  .fit-section,
  .team-section,
  .hiring-band,
  .cta-band {
    padding: 22px 18px;
    border-radius: 22px;
  }

  .about-hero-copy {
    padding: 0;
  }

  .hero-image-card,
  .founder-photo-card,
  .team-card {
    border-radius: 18px;
  }

  .cta-actions {
    justify-content: stretch;
  }

  .cta-btn {
    width: 100%;
  }
}
</style>
